import { useState } from "react";
import { useTranslation } from "react-i18next";

import {
  Popover,
  PopoverAnchor,
  PopoverContent,
} from "@/components/ui/popover";
import { useDashboardView } from "@/hooks/useDashboardView";
import { DashboardViewToggle } from "@/pages/dashboard/DashboardViewToggle";

const VIEW_HINT_DISMISSED_KEY = "fiscalo:dashboard-view-hint-dismissed";

interface DashboardViewControlProps {
  /**
   * "header" is the desktop topbar slot, sized to its content.
   * "block" is the mobile slot in the dashboard body, full width.
   */
  placement: "header" | "block";
}

/**
 * The view toggle plus its one-time hint.
 *
 * These are one component because the hint is a popover anchored to the
 * toggle: separating them means whoever renders the toggle also has to
 * re-implement the anchor, the dismissal and the localStorage key. It moved
 * out of Header.tsx when the toggle gained a second home on mobile.
 *
 * Render this in exactly ONE place at a time. Two mounted copies would open
 * two popovers, one of them anchored to something the user cannot see.
 */
export function DashboardViewControl({
  placement,
}: DashboardViewControlProps): JSX.Element {
  const { t } = useTranslation();
  const { view, setView, isDefaulted } = useDashboardView();

  const [hintDismissed, setHintDismissed] = useState<boolean>(() => {
    if (typeof window === "undefined") return true;
    try {
      return localStorage.getItem(VIEW_HINT_DISMISSED_KEY) === "1";
    } catch {
      return false;
    }
  });

  const dismissHint = (): void => {
    setHintDismissed(true);
    try {
      localStorage.setItem(VIEW_HINT_DISMISSED_KEY, "1");
    } catch {
      // Storage unavailable — hidden for this session only.
    }
  };

  const isBlock = placement === "block";

  return (
    <Popover
      open={isDefaulted && !hintDismissed}
      onOpenChange={(next) => {
        if (!next) dismissHint();
      }}
    >
      {/* Anchor, not Trigger: the toggle must keep its own click behaviour —
          wrapping it in a trigger would make choosing a view also toggle the
          popover. */}
      <PopoverAnchor asChild>
        <div className={isBlock ? "w-full" : undefined}>
          <DashboardViewToggle
            view={view}
            fullWidth={isBlock}
            onChange={(next) => {
              // Picking a view IS finding the control, so the nudge has done
              // its job.
              dismissHint();
              setView(next);
            }}
          />
        </div>
      </PopoverAnchor>
      <PopoverContent
        align={isBlock ? "center" : "end"}
        side="bottom"
        className={isBlock ? "w-[min(20rem,calc(100vw-2rem))]" : "w-80"}
      >
        <p className="text-sm text-[var(--fx-ink-700)] dark:text-[var(--dk-text-soft)]">
          {t(
            "dashboard.view.hintShort",
            "Estás en la vista simple. Cambiá a “Avanzada” para ver todos los paneles.",
          )}
        </p>
        <div className="mt-3 flex justify-end">
          <button
            type="button"
            className="fx-btn fx-btn-accent fx-btn-sm"
            onClick={dismissHint}
          >
            {t("dashboard.view.hintDismiss", "Entendido")}
          </button>
        </div>
      </PopoverContent>
    </Popover>
  );
}
